<template>
  <v-sheet class="wrapper">
    <v-row>
      <v-col>
        <v-btn
          text
          :loading="loading"
          @click="onRefresh"
        >
          <v-icon left>
            mdi-refresh
          </v-icon>
          {{ $t('alertCase.buttons.refresh') }}
        </v-btn>
        <v-btn
          @click="onExport"
          text
        >
          <v-icon left>
            mdi-content-save
          </v-icon>
          {{ $t('alertCase.buttons.export') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      class="elevation-1 col-12"
      v-model="selected_records"
      show-select
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-spacer />
          <v-btn
            v-if="selected_records.length > 0"
            color="error"
            class="mb-2"
            @click="() => {
              dialog_bulk_action = true
            }"
          >
            <v-icon left>
              mdi-checkbox-multiple-marked-outline
            </v-icon>
            {{ $t('alertCase.buttons.bulk_action') }} ({{ selected_records.length }})
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:[`item.entity_type`]="{ item }">
        <v-icon
          v-if="['PERSON', 'Individual'].find(type => type.toLowerCase() === item.entity_type.toLowerCase())"
          color="blue lighten-3"
        >
          mdi-account
        </v-icon>
        <v-icon
          v-else-if="['ENTITY', 'Corporate'].find(type => type.toLowerCase() === item.entity_type.toLowerCase())"
          color="blue lighten-3"
        >
          mdi-bank
        </v-icon>
      </template>
      <template v-slot:[`item.entity_name`]="{ item }">
        <div>
          <span class="font-weight-bold">{{ item.entity_name }}</span>
        </div>
        <small class="caption grey--text">
          {{ $t('ewe_screen.entities_table.column.entityId.label') }}: {{ item.entity_id }}
        </small>
      </template>
      <template v-slot:[`item.entity_picture`]="{ item }">
        <v-menu
          :nudge-width="200"
          bottom
          right
          offset-x
          open-on-hover
          :disabled="!item.entity_picture"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-img
              v-bind="attrs"
              v-on="on"
              slot="activator"
              max-width="45"
              class="my-2"
              :src="item.entity_picture || defaultPortraitIcon"
              aspect-ratio="1"
              :contain="item.entity_picture ? false : true"
              :style="item.entity_picture ? 'cursor: zoom-in' : ''"
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="grey"
                  />
                </v-row>
              </template>
            </v-img>
          </template>
          <v-card>
            <v-img
              contain
              max-width="245"
              :src="item.entity_picture || defaultPortraitIcon"
            />
          </v-card>
        </v-menu>
      </template>
      <template v-slot:[`item.entity_categories`]="{ item }">
        <ScreeningCategoryChips :category="item.entity_categories" />
      </template>
      <template v-slot:[`item.entity_dob`]="{ item }">
        {{ `${(item.entity_dob && item.entity_dob.length >= 4 ? item.entity_dob : null) || 'NA'}` }}
      </template>
      <template v-slot:[`item.entity_countries`]="{ item }">
        <span
          v-for="(country, index) in (item.entity_countries || []).slice(0, 3)"
          :key="index"
          class="mr-1"
        >
          <flag
            :iso="country.iso2"
            :title="($i18n.locale.toLowerCase() === 'zh-hant' ? country.displayNameZhHant : country.displayName)"
            :squared="false"
            style="font-size:20px"
          />
        </span>
        <span
          v-if="(item.entity_countries || []).length > 3"
          class="grey--text text-caption"
        >
          <br>
          + {{ (item.entity_country || []).length - 3 }} countr(ies)
        </span>
        <span
          v-if="(item.entity_countries || []).length === 0"
        >
          N/A
        </span>
      </template>
      <template v-slot:[`item.similiarity`]="{ item }">
        <ScreeningResultsSimilarity :similarity-level="item.similarity_level" />
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <ScreeningStatusChips :status="item.status" />
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <v-btn
          icon
          @click="() => {
            dialog_detail_record = item
            dialog_detail = true
          }"
          data-cy="btn_viewAlertCaseEntity"
        >
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog
      v-model="dialog_detail"
      max-width="90%"
      persistent
    >
      <template v-slot:default="dialog">
        <v-card>
          <PopupAlertCaseEntity
            :records="dialog_detail_record"
            @onSave="(form) => {
              getDataFromApi()
              dialog_detail = false
              dialog_detail_record = null
            }"
            @onClose="() => {
              dialog.value = false
              dialog_detail = false
              dialog_detail_record = null
            }"
            data-cy="alert_detail"
          />
        </v-card>
      </template>
    </v-dialog>
    <v-dialog
      v-model="dialog_bulk_action"
      max-width="640px"
      persistent
      @keydown.esc="() => {
        // Prevent closing the dialog if a request is in progress
        if ($refs.case_entity_bulk_action.requestLoading) return

        dialog_bulk_action = false
        $refs.case_entity_bulk_action.clearForm()
      }"
    >
      <template v-slot:default="dialog">
        <v-card>
          <PopupAlertCaseEntityBulkAction
            ref="case_entity_bulk_action"
            :records="selected_records"
            @onSuccess="() => {
              getDataFromApi()
              dialog_bulk_action = false
              selected_records = []
            }"
            @onClose="() => {
              dialog.value = false
              dialog_bulk_action = false
            }"
            data-cy="alert_case_entity_bulk_action"
          />
        </v-card>
      </template>
    </v-dialog>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ScreeningCategoryChips from '@/components/common/ScreeningCategoryChips'
import DefaultPortraitIcon from '@/assets/default_portrait_picture.svg'
import PopupAlertCaseEntity from '@/components/popups/PopupAlertCaseEntity'
import PopupAlertCaseEntityBulkAction from '@/components/popups/PopupAlertCaseEntityBulkAction'
import ScreeningStatusChips from '../common/ScreeningStatusChips.vue'
import ScreeningResultsSimilarity from '../common/ScreeningResultsSimilarity.vue'

export default {
  components: {
    ScreeningCategoryChips,
    ScreeningStatusChips,
    PopupAlertCaseEntity,
    PopupAlertCaseEntityBulkAction,
    ScreeningResultsSimilarity
  },
  props: {
    caseId: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    ...mapGetters('auth', [
      'username'
    ]),
    ...mapState('auth', [
      'organization_name'
    ]),
    headers () {
      return [
        { text: '', value: 'data-table-select', sortable: false },
        {
          text: this.$t('alert_case_details.table.headers.entity_type.label'),
          align: 'start',
          sortable: false,
          value: 'entity_type'
        },
        { text: this.$t('alert_case_details.table.headers.entity_name.label'), value: 'entity_name' },
        { text: this.$t('ewe_screen.entities_table.header.similarity'), value: 'similiarity', sortable: false },
        { text: this.$t('alert_case_details.table.headers.entity_picture.label'), sortable: false, value: 'entity_picture' },
        { text: this.$t('alert_case_details.table.headers.entity_categories.label'), value: 'entity_categories' },
        { text: this.$t('alert_case_details.table.headers.entity_dob.label'), value: 'entity_dob' },
        { text: this.$t('alert_case_details.table.headers.entity_countries.label'), value: 'entity_countries' },
        { text: this.$t('alert_case_details.table.headers.status.label'), value: 'status' },
        { text: this.$t('listing.table.action.label'), sortable: false, value: 'action', width: '80px' }
      ]
    },
    defaultPortraitIcon () {
      return DefaultPortraitIcon
    }
  },
  data () {
    return {
      totalItems: 0,
      items: [],
      loading: true,
      options: {},
      dialog_detail: false,
      dialog_detail_record: null,
      dialog_bulk_action: false,
      selected_records: []
    }
  },
  watch: {
    caseId () {
      this.getDataFromApi()
    },
    options: {
      handler () {
        this.getDataFromApi()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      getAlertCaseEntities: 'alert_case/findWhereEntities',
      countAlertCaseEntities: 'alert_case/countEnities',
      getCompany: 'company/findOne',
      getNaturalPerson: 'natural_person/findOne',
      getAlertCase: 'alert_case/findOne',
      getScreeningResultsEntity: 'scan/getScreeningResultsEntity'
    }),
    // ...mapActions('scan', [
    //   // 'nameScreening',
    //   // 'nameScreeningEWE',
    //   // 'getScreeningResults',
    //   'getScreeningResultsEntity'
    //   // 'fetchScreeningList',
    //   // 'fetchCategories'
    // ]),
    getDataFromApi () {
      this.loading = true
      Promise.all([
        this.getAlertCaseEntities({
          parent: this.caseId,
          criteria: {
            ...(this.options.itemsPerPage > 0 && {
              limit: this.options.itemsPerPage,
              skip: (parseInt(this.options.page) - 1) * this.options.itemsPerPage
            }),
            sort: this.options.sortBy.length ? this.options.sortBy.map((sortBy, key) => {
              return `${sortBy} ${this.options.sortDesc[key] ? 'desc' : 'asc'}`
            }) : ['entity_id desc']
          }
        }),
        this.countAlertCaseEntities({
          parent: this.caseId,
          criteria: {}
        })
      ])
        .then(([result, resultCnt]) => {
          this.items = result.data
          this.totalItems = resultCnt.data.count
          this.loading = false
        })
    },
    async onRefresh () {
      this.getDataFromApi()
      this.$emit('onRefresh')
    },
    async onExport () {
      const alertCase = await this.getAlertCase(this.caseId).then(res => {
        return res.data
      })
      const company = alertCase.company ? (await this.getCompany(alertCase.company.id).then(res => {
        return res.data
      })) : null
      const person = alertCase.person ? (await this.getNaturalPerson(alertCase.person.id).then(res => {
        return res.data
      })) : null
      const routeData = this.$router.resolve({ name: 'ReportAlertCase', query: { } })
      this.windowRef = window.open(routeData.href, '', 'width=920,height=800,left=200,top=200')
      window.childWindowReady = async () => {
        const { data: { count } } = await this.countAlertCaseEntities({
          parent: this.caseId,
          criteria: {}
        })
        const cases = new Set()
        let page = 1
        while (cases.size < count) {
          const { data } = await this.getAlertCaseEntities({
            parent: this.caseId,
            criteria: {
              limit: 10,
              skip: (parseInt(page) - 1) * 10,
              sort: this.options.sortBy.length ? this.options.sortBy.map((sortBy, key) => {
                return `${sortBy} ${this.options.sortDesc[key] ? 'desc' : 'asc'}`
              }) : ['entity_id desc']
            }
          })
          data.forEach(item => {
            cases.add(item)
          })
          page++
        }

        this.windowRef.setReportData({
          person,
          company,
          alert_case: alertCase,
          alert_case_entities: Array.from(cases),
          entities: await Promise.all(Array.from(cases).map(item => {
            return this.getScreeningResultsEntity({
              scanID: item.screening_encrypt_id,
              entityID: item.entity_id
            }).then(res => {
              return res.data
            })
          }))
        }, this.organization_name, this.username)
      }
    }
  },
  mounted () {
    this.$store.commit('scan/CLEAR_CACHED_ENTITY_DETAILS')
  }
}
</script>

<style scoped>
.wrapper {
  width: 100%;
}

</style>
